/**
 * Add to Mimir's routes (ACP-D48, docs/gfx-mimir-publish/plan.md; spread into `ROUTES` by `routes.ts`). Cloud only.
 * Kept out of `GRAPHICS_ROUTES`: those are the shared handlers' table (`@acp/handlers/graphicsHandlers.js`), and this
 * one is the cloud's own (`apps/cloud-api/src/graphics/mimir.ts`).
 */
import { graphicsTemplateVersionPathWireSchema } from './graphics.js';
import {
  addGraphicsTemplateVersionToMimirRequestWireSchema,
  addGraphicsTemplateVersionToMimirResponseWireSchema,
  graphicsMimirStatusResponseWireSchema,
} from './graphicsMimir.js';
import type { RouteDef } from './routes.js';

export const GRAPHICS_MIMIR_ROUTES = {
  addGraphicsTemplateVersionToMimir: {
    id: 'addGraphicsTemplateVersionToMimir',
    method: 'POST',
    path: '/graphics/templates/{templateId}/versions/{version}/mimir',
    tag: 'graphics',
    summary: 'Add a template version to Mimir',
    description:
      'Makes the published version a NEW graphics item in the stage’s Mimir (every call makes another; none is updated): creates the item, uploads the `.gfxpkg`, stores its template and, given `thumbnailSha256`, its thumbnail; records the item id on the version (`mimirItems`). `not-found` for an unknown version (a retired template’s versions still count). `conflict` when the version’s scene schema is newer than the hosted Mimir module renders (the `schemaVersion` of its `gfx-mimir.json`; deploy a newer module). `unavailable` when the stack has no Mimir settings or its secret has no value (retry after an hour: the stack’s configuration), when the hosted module’s `gfx-mimir.json` cannot be read (retry after a minute), or when Mimir refused or failed a call (retry after a minute): the message names the call and Mimir’s HTTP status, secrets redacted, and says whether the half-made item was deleted. (The existing codes, not new ones: a new code would widen every route’s error enum.)',
    params: graphicsTemplateVersionPathWireSchema,
    query: null,
    body: addGraphicsTemplateVersionToMimirRequestWireSchema,
    response: addGraphicsTemplateVersionToMimirResponseWireSchema,
    successStatus: 201,
    permission: 'graphics.mimir',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'not-found', 'conflict'],
  },
  getGraphicsMimirStatus: {
    id: 'getGraphicsMimirStatus',
    method: 'GET',
    path: '/graphics/mimir/status',
    tag: 'graphics',
    summary: 'Whether Add to Mimir is available',
    description: 'Whether this stack is configured for Mimir and whether the caller holds `graphics.mimir`: the designer shows Add to Mimir only when both are true. Needs `graphics.read` (anyone who can open the designer’s library).',
    params: null,
    query: null,
    body: null,
    response: graphicsMimirStatusResponseWireSchema,
    successStatus: 200,
    permission: 'graphics.read',
    permissionScope: 'tenant',
    errors: [],
  },
} as const satisfies Record<string, RouteDef>;
